import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Card, Button } from '../ui';

export default function ImageDisplay({
  title,
  subtitle,
  url,
  content,
  description,
  className,
}) {
  const { t } = useTranslation();
  const [isZoomed, setIsZoomed] = useState(false);
  const imageUrl = url || content;

  return (
    <>
      <Card
        gradient
        elevated
        className={cn('overflow-hidden', className)}
        padding="none"
      >
        <div className="bg-gray-100 dark:bg-gray-700 p-6 sm:p-8">
          {imageUrl ? (
            <div className="relative flex justify-center">
              <img
                src={imageUrl}
                alt={title || t('stimulus.imageContent')}
                className={cn(
                  'max-w-full max-h-[60vh] object-contain rounded-lg',
                  'shadow-[0_4px_16px_rgba(0,0,0,0.15)]',
                  'cursor-zoom-in transition-transform duration-200 hover:scale-[1.02]'
                )}
                onClick={() => setIsZoomed(true)}
              />
              <button
                onClick={() => setIsZoomed(true)}
                className={cn(
                  'absolute bottom-4 right-4',
                  'px-3 py-1.5 rounded-lg',
                  'bg-black/70 text-white text-sm',
                  'hover:bg-black/80 transition-colors',
                  'flex items-center gap-1.5'
                )}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                  />
                </svg>
                {t('stimulus.expand')}
              </button>
            </div>
          ) : (
            <div className="w-full aspect-[4/3] bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
              <div className="text-center text-gray-400 dark:text-gray-500">
                <svg
                  className="w-16 h-16 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p>{t('stimulus.imageUnavailable')}</p>
              </div>
            </div>
          )}
        </div>
        <div className="p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">{subtitle}</p>
          )}
          {description && (
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">{description}</p>
          )}
        </div>
      </Card>

      {/* Zoom Modal */}
      {isZoomed && imageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <button
            onClick={() => setIsZoomed(false)}
            className={cn(
              'absolute top-4 right-4',
              'px-4 py-2 rounded-lg',
              'bg-white/10 text-white',
              'hover:bg-white/20 transition-colors',
              'flex items-center gap-2'
            )}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            {t('common.close')}
          </button>
          <img
            src={imageUrl}
            alt={title || t('stimulus.imageContent')}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
